import { Component, HostListener, inject, PLATFORM_ID, AfterViewInit } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { OnInit, OnDestroy, viewChild, ElementRef } from '@angular/core';
import { CreditsService } from '../../../../core/services/credits.service';
import { ActivatedRoute } from '@angular/router';
import { Utils } from '../../../../shared/utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { environment } from '../../../../../environments/environment';
import { ViewChild } from '@angular/core';
import { LanguageService } from '../../../../core/services/language/language.service';

import { RouterModule } from '@angular/router';
import { CardSkeleton } from '../../../../layout/designs/card-skeleton/card-skeleton';

@Component({
  selector: 'app-tv-credits',
  imports: [CommonModule, RouterModule, CardSkeleton],
  templateUrl: './tv-credits.html',
  styleUrl: './tv-credits.scss',
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('fadeAndToggle', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      state('active', style({ transform: 'scale(1.1)', background: 'linear-gradient(340deg, #96f2d7, #96f2d7, #38d9a9, #12b886)', borderRadius: '2.5em' })),
      state('inactive', style({ transform: 'scale(1)', backgroundColor: '#f8f9fa', borderRadius: '2.5em' })),
      transition('inactive => active', [
        animate('200ms cubic-bezier(.68,-0.55,.27,1.55)')
      ]),
      transition('active => inactive', [
        animate('200ms ease-out')
      ])
    ])
  ]
})
export class TvCredits implements OnInit, AfterViewInit {

  isMobile: boolean = false;
  canScrollLeft: boolean = false;
  canScrollRight: boolean = false;
  activeCardIndex: number | null = null;
  creditData: any;
  movieId: any;
  movieName: any;
  isLoading: boolean = true;
  initialSkeletons: number[] = Array(8).fill(0);
  viewportWidth: number = 0;

  frontendUrl: string = environment.frontendUrl;
  backendUrl: string = environment.backendUrl;
  errorImagePath: string = '../../../../../assets/images/not_found.png';
  isBlurred = false;
  
  private platformId = inject(PLATFORM_ID);
  public readonly languageService = inject(LanguageService);
  private hoveredIndex: number | null = null;
  
  private destroy$ = new Subject<void>();
  
  constructor(private creditService: CreditsService, private route: ActivatedRoute, private utils: Utils) { }
  @ViewChild('trendingMovieSection') trendingMovieSection!: ElementRef;
  
  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const slug = params.get('slug');
      this.getTvCredits(slug);
    });

    this.languageService.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.movieId) {
        this.getTvCredits();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.viewportWidth = window.innerWidth;
    setTimeout(() => this.checkScroll(), 250);
  }

  @HostListener('window:load')
  load() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.viewportWidth = window.innerWidth;
    setTimeout(() => this.checkScroll(), 250);
  }

  @HostListener('window:resize')
  onResize() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.viewportWidth = window.innerWidth;
    setTimeout(() => this.checkScroll(), 150);
  }

  onImageError(event: Event) {
    return this.utils.onImageVerticalError(event);
  }


  onImageLoad(event: Event) {
    const target = event.target as HTMLImageElement;
    target.src = this.errorImagePath
  }

  formattedPersonName(sentence: string): string {
    return this.utils.movieNameSplitter(sentence);
  }

  getPersonRoute(credit: any): (string | number)[] {
    const slug = `${credit?.id}-${this.utils.movieNameSplitter(credit?.name || 'person') || 'person'}`;
    return ['/person', slug];
  }

  getTvCredits(slugParam?: string | null) {
    const slug = slugParam !== undefined ? slugParam : this.route.snapshot.paramMap.get('slug');

    if (slug) {
      const [id, name] = slug.split('-');
      this.movieId = id;
      this.movieName = name;
    }

    if (this.movieId) {
      this.isLoading = true;
      const lang = this.languageService.currentLanguage?.code || 'en-US';
      this.creditService.getCredits(parseInt(this.movieId), 'tv', lang).subscribe({
        next: (data) => {
          this.creditData = data?.cast || [];
          this.isLoading = false;
          setTimeout(() => this.checkScroll(), 200);
        },
        error: () => {
          this.isLoading = false;
        }
      });
    }
  }

  checkScroll(): void {
    if (!this.trendingMovieSection?.nativeElement) return;
    const el = this.trendingMovieSection.nativeElement;
    this.canScrollLeft = el.scrollLeft > 10;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 10;
  }

  scrollLeft(): void {
    if (this.trendingMovieSection) {
      this.trendingMovieSection.nativeElement.scrollBy({
        left: -300,
        behavior: 'smooth'
      });
      setTimeout(() => this.checkScroll(), 350);
    }
  }

  scrollRight(): void {
    if (this.trendingMovieSection) {
      this.trendingMovieSection.nativeElement.scrollBy({
        left: 300,
        behavior: 'smooth'
      });
      setTimeout(() => this.checkScroll(), 350);
    }
  }

  onMouseEnter(index: number): void {
    this.hoveredIndex = index;
    this.isBlurred = true;
  }

  onMouseLeave(): void {
    this.hoveredIndex = null;
    this.isBlurred = false;
  }

}